/**
 * Inspired by the Material Design Form Field:
 * https://github.com/angular/components/blob/master/src/material/form-field/form-field.ts
 * https://material.angular.io/components/form-field/examples
 */
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, ContentChild, ContentChildren, HostBinding, Input, QueryList } from '@angular/core';
import { NgControl, RequiredValidator } from '@angular/forms';
import { Observable } from 'rxjs';
import { MuiI18nProvider } from '../../i18n';
import { MuiIconFont, MuiIconModule } from '../../icon';
import { MuiPopoverModule } from '../../popover';
import { MuiInputBaseDirective } from '../input/mui-input-base.directive';
import { MuiInputDirective } from '../input/mui-input.directive';
import { MuiPasswordChecker } from '../password-checker/password-checker.component';
import { isControlValid } from '../utils';
import { MuiFormFieldError } from './error/mui-form-field-error.component';
import { MuiFormFieldHint } from './hint/mui-form-field-hint.component';
import { muiFormFieldErrorsAnimation } from './mui-form-field-error-animation';
import { MuiFormFieldSubmit } from './submit/mui-form-field-submit.component';


export enum MuiFormFieldSize {
  DEFAULT = 'DEFAULT',
  LARGE = 'LARGE'
}

@Component({
  selector: 'mui-form-field, [mui-form-field]',
  templateUrl: './mui-form-field.component.html',
  styleUrls: ['./mui-form-field.component.scss'],
  animations: [muiFormFieldErrorsAnimation],
  imports: [
    CommonModule,
    MuiIconModule,
    MuiPopoverModule
  ],
  standalone: true
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class MuiFormField<T = any> implements AfterContentInit {
  @Input()
  size: MuiFormFieldSize | string = MuiFormFieldSize.DEFAULT;

  @Input()
  iconFont: MuiIconFont | string = MuiIconFont.FONTAWESOME;

  @Input()
  required = false;

  @Input()
  popoverText?: string;

  @Input()
  popoverPlacement: 'top' | 'end' | 'bottom' | 'start' | 'auto' = 'auto';

  @Input()
  @HostBinding('class.has--label')
  label: string | null = null;

  @Input()
  labelId: string | null = null;

  @Input()
  labelFor: string | null = null;

  @Input()
  showPasswordButtonLabel?: string;

  @Input()
  hidePasswordButtonLabel?: string;

  @Input()
  isResettable = false;

  @Input()
  formResetValue?: T;

  @Input()
  @HostBinding('class.has--icon')
  icon?: string;

  @Input()
  @HostBinding('class.mui-input--inline')
  inline = false;

  @Input()
  @HostBinding('class.mui-input--max-width')
  maxWidth = false;

  @HostBinding('class.mui-input')
  readonly _muiBaseClass = true;

  @HostBinding('class.is--large')
  get isLarge(): boolean {
    return this.size === MuiFormFieldSize.LARGE;
  }

  @HostBinding('class.has--trailing-icon')
  get hasTrailingIcon(): boolean {
    return !this.isControlValid || this.isSelect || this.hasSubmitButton || this.isPassword || this.hasResetButton;
  }

  @ContentChild(MuiInputBaseDirective)
  muiInput?: MuiInputBaseDirective;

  @ContentChild(MuiFormFieldSubmit)
  muiSubmit?: MuiFormFieldSubmit;

  @ContentChild(MuiPasswordChecker)
  passwordChecker?: MuiPasswordChecker;

  @ContentChildren(MuiFormFieldError)
  muiErrors: QueryList<MuiFormFieldError> = new QueryList();

  showPassword = false;

  @ContentChild(MuiFormFieldHint)
  protected muiFormFieldHint?: MuiFormFieldHint;

  @ContentChild(RequiredValidator)
  private _requiredValidator?: RequiredValidator;

  @ContentChild(NgControl)
  private _control?: NgControl;

  get isControlValid(): boolean {
    return this._control ? isControlValid(this._control) : true;
  }

  get isControlRequired(): boolean {
    return this._requiredValidator ? coerceBooleanProperty(this._requiredValidator.required) : this.required;
  }

  get isSelect(): boolean {
    if (!(this.muiInput instanceof MuiInputDirective)) {
      return false;
    }

    return this.muiInput && this.muiInput.tag === 'select';
  }

  get isTextarea(): boolean {
    if (!(this.muiInput instanceof MuiInputDirective)) {
      return false;
    }

    return this.muiInput && this.muiInput.tag === 'textarea';
  }

  get isPassword(): boolean {
    if (!(this.muiInput instanceof MuiInputDirective)) {
      return false;
    }

    return this.muiInput && this.muiInput.hasPasswordToggle;
  }

  get hasSubmitButton(): boolean {
    return !!this.muiSubmit;
  }

  get hasResetButton(): boolean {
    return this.isResettable && !!this.muiInput?.value;
  }

  protected readonly translations$: Observable<{ showPasswordButton: string; hidePasswordButton: string }>;

  constructor(private _i18n: MuiI18nProvider) {
    this.translations$ = this._i18n.password();
  }

  ngAfterContentInit(): void {
    // Connect input field and submit button
    if (this.muiSubmit && this.hasSubmitButton && this.muiInput && this.muiInput instanceof MuiInputDirective) {
      this.muiSubmit.muiInput = this.muiInput;
      this.muiInput.muiSubmit = this.muiSubmit;
    }
  }

  resetInput(): void {
    this._control?.control?.setValue(this.formResetValue || null);
    this._control?.control?.markAsDirty();
    this.muiInput?.clear();
  }

  markAsSaving(): void {
    if (this.muiSubmit) {
      this.muiSubmit.saving = true;
    }
  }

  markAsSaved(newValue?: any): void {
    if (this.muiSubmit) {
      this.muiSubmit.saving = false;
    }

    if (this.muiInput) {
      this.muiInput.blur();

      if (this.muiInput instanceof MuiInputDirective && this.muiInput.parentForm) {
        this.muiInput.parentForm.form.markAsPristine();
      } else if (this.muiInput.ngControl) {
        this.muiInput.ngControl.control?.markAsPristine();
      }
    }
  }

  markAsSavingFailed(): void {
    if (this.muiSubmit) {
      this.muiSubmit.saving = false;
    }
  }

  togglePwd(): void {
    if (this.muiInput instanceof MuiInputDirective) {
      this.showPassword = !this.showPassword;
      this.muiInput.type = this.showPassword ? 'text' : 'password';
    }
  }

  protected displayErrorMessages(): boolean {
    return this.muiErrors.length > 0 && !this.isControlValid;
  }
}
